import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    OnInit,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
    get as _get,
    toNumber as _toNumber,
    isFinite as _isFinite,
} from 'lodash';
import { FormatNumberPipeModule } from 'src/app/shared/pipes/format-number.pipe';
import {
    ChannelPricePlan,
    GENERAL4,
    LnwPdata,
    effectivePrice,
    general4FromBase,
    planChannelPrices,
} from 'src/app/shared/config/channel-price-rules';
import { PriceCalSaveService } from '../price-cal-filler/price-cal.service';

/**
 * ตั้งราคาทุกช่องทางของสินค้าจากราคาหลักช่องเดียว — ฝังในหน้าสินค้า lnwshop ผ่าน
 * `embeded_price_calculator.user.js` (iframe เดียวกับเครื่องคิดเลข ใช้ scope เดียวกัน)
 *
 * กรอกราคาหลัก → General4 ตามสูตรเครื่องคิดเลข (แก้ทับได้) → ช่องอื่นตามกฎ align
 * (`shared/config/channel-price-rules.ts`) → ปุ่ม "ตั้งราคาบนหน้า" ส่งรายการให้ userscript กรอกลงฟอร์ม
 * ของ lnwshop ทีละแท็บ **โดยไม่กดบันทึก** — คนตรวจแล้วกดบันทึกเอง
 *
 * สัญญา postMessage (อีกฝั่งอยู่ในรีโป lnwshop_tampermokey_scripts — แก้ข้างหนึ่งต้องแก้อีกข้าง):
 *   iframe → host  { scope, status: 'request' }                      ขอ pdata (หน้าถูกเปิดจากลิงก์ในเครื่องคิดเลข
 *                                                                     ไม่ได้ผ่าน load ของ iframe จึงไม่มี handshake)
 *   host  → iframe { scope, status: 'send_data', data: vm.pdata }
 *   iframe → host  { scope, status: 'received' }
 *   iframe → host  { scope, action: 'apply-prices', param: ApplyRequest }
 *   host  → iframe { scope, status: 'applied', results: ApplyResult[] }
 *   iframe → host  { scope, status: 'resize', height }
 */

export interface ApplyRequest {
    pid: number;
    base: number | null; // null = ไม่แตะราคาหลัก
    channels: {
        channel: string;
        instance: string;
        label: string;
        kind: 'set' | 'sync';
        price: number;
    }[];
}

export interface ApplyResult {
    target: string; // 'base' หรือ instance id
    label: string;
    ok: boolean;
    message: string; // สิ่งที่ทำ / เหตุที่ไม่ทำ
}

@Component({
    selector: 'app-price-set-filler',
    templateUrl: './price-set-filler.component.html',
    styleUrls: ['./price-set-filler.component.scss'],
    imports: [CommonModule, RouterLink, FormatNumberPipeModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [PriceCalSaveService],
})
export class PriceSetFillerComponent implements OnInit {
    // scope เดียวกับเครื่องคิดเลข: iframe เดียวกัน userscript ตัวเดียวกัน
    SCOPE = 'price-cal-filler';
    GENERAL4 = GENERAL4;

    pdata: LnwPdata | null = null;
    pid = 0;
    code = '-';
    canEditBase = true; // สินค้าลูกที่ราคาตามแม่ไม่มีช่องราคาหลักให้แก้
    currentBase: number | null = null;
    currentGeneral4: number | null = null;

    baseInput = '';
    general4Input = '';
    // ค่ากลางของเครื่องคิดเลข (pid -1) — ค่าเริ่มต้นเท่าเครื่องคิดเลข
    platformFeePct = 22;
    vatPct = 7;
    plan: ChannelPricePlan[] = [];
    results: ApplyResult[] | null = null;
    applying = false;
    statusText = '';

    private messageSourceWindow: Window | null = null;
    private requestTimer: ReturnType<typeof setInterval> | null = null;
    private cdref = inject(ChangeDetectorRef);
    private route = inject(ActivatedRoute);
    private priceCalSaveService = inject(PriceCalSaveService);

    ngOnInit(): void {
        // ราคาหลักที่เครื่องคิดเลขส่งมากับลิงก์ (ราคาขาย LS ปัดขึ้น) — ใช้แทนราคาหลักปัจจุบันของสินค้า
        const fromCalc = _toNumber(
            this.route.snapshot.queryParamMap.get('base'),
        );
        if (_isFinite(fromCalc) && fromCalc > 0)
            this.baseInput = String(fromCalc);
        // ขอ pdata ซ้ำทุกวินาทีจนได้ — host ตอบเฉพาะเมื่อ vm.pdata พร้อม
        this.requestTimer = setInterval(
            () => this.post({ status: 'request' }),
            1000,
        );
        this.post({ status: 'request' });
    }

    private post(msg: Record<string, unknown>): void {
        const target = this.messageSourceWindow ?? window.parent;
        if (target && target !== window)
            target.postMessage({ scope: this.SCOPE, ...msg }, '*');
    }

    @HostListener('window:message', ['$event'])
    onMessage(event: MessageEvent): void {
        if (_get(event, 'data.scope') !== this.SCOPE) return;
        const status = _get(event, 'data.status');
        if (status === 'send_data' && _get(event, 'data.data')) {
            this.messageSourceWindow = event.source as Window;
            this.post({ status: 'received' });
            if (this.requestTimer) {
                clearInterval(this.requestTimer);
                this.requestTimer = null;
            }
            this.loadPdata(_get(event, 'data.data') as LnwPdata);
        } else if (status === 'applied') {
            this.applying = false;
            this.results = (_get(event, 'data.results') as ApplyResult[]) ?? [];
            const failed = this.results.filter((r) => !r.ok).length;
            this.statusText = failed
                ? `กรอกไม่ครบ ${failed} รายการ — ดูรายละเอียดด้านล่าง`
                : 'กรอกครบแล้ว ตรวจแล้วกด "บันทึก" บนหน้า lnwshop';
            this.cdref.detectChanges();
            this.resize();
        }
    }

    private loadPdata(pdata: LnwPdata): void {
        const pid = _toNumber(pdata.id);
        if (!_isFinite(pid) || pid <= 0) return;
        // หลังกดตั้งราคา host ส่ง pdata ซ้ำได้ (handshake ของ iframe โหลดใหม่) — อย่าทับค่าที่คนกรอก
        const first = this.pid !== pid;
        this.pdata = pdata;
        this.pid = pid;
        this.code = _get(pdata, 'props.code', '-');
        this.canEditBase = Number(_get(pdata, 'props.can_edit_price', 1)) !== 0;
        this.currentBase = effectivePrice(pdata, 'lnwshop', '') ?? null;
        this.currentGeneral4 = effectivePrice(
            pdata,
            GENERAL4.channel,
            GENERAL4.instance,
        );
        if (first) {
            if (!this.baseInput && this.currentBase !== null)
                this.baseInput = String(this.currentBase);
            this.priceCalSaveService.load(pid).subscribe((params) => {
                const fee = _toNumber(_get(params, 'platformFeePct'));
                const vat = _toNumber(_get(params, 'vatPct'));
                if (_isFinite(fee)) this.platformFeePct = fee;
                if (_isFinite(vat)) this.vatPct = vat;
                this.onBaseChange(this.baseInput);
            });
        }
        this.onBaseChange(this.baseInput);
    }

    get base(): number | null {
        const n = _toNumber(this.baseInput);
        return _isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null;
    }

    get general4(): number | null {
        const n = _toNumber(this.general4Input);
        return _isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null;
    }

    get baseChanged(): boolean {
        return (
            this.base !== null &&
            (this.currentBase === null ||
                Math.abs(this.base - this.currentBase) >= 0.005)
        );
    }

    get general4Changed(): boolean {
        return (
            this.general4 !== null &&
            (this.currentGeneral4 === null ||
                Math.abs(this.general4 - this.currentGeneral4) >= 0.005)
        );
    }

    get changedCount(): number {
        return (
            this.plan.filter((p) => p.changed).length +
            (this.baseChanged && this.canEditBase ? 1 : 0) +
            (this.general4Changed ? 1 : 0)
        );
    }

    /** ราคาหลักเปลี่ยน → General4 ตามสูตร → ช่องอื่นตามกฎ */
    onBaseChange(value: string): void {
        this.baseInput = value;
        const g4 =
            this.base === null
                ? null
                : general4FromBase(this.base, this.platformFeePct, this.vatPct);
        this.general4Input = g4 === null ? '' : String(g4);
        this.replan();
    }

    /** คนแก้ General4 เอง → ช่องอื่นตามกฎจากค่าที่แก้ ราคาหลักคงเดิม */
    onGeneral4Change(value: string): void {
        this.general4Input = value;
        this.replan();
    }

    private replan(): void {
        this.results = null;
        this.statusText = '';
        this.plan =
            this.pdata && this.base !== null && this.general4 !== null
                ? planChannelPrices(this.pdata, this.base, this.general4)
                : [];
        this.cdref.detectChanges();
        this.resize();
    }

    apply(): void {
        if (
            !this.pdata ||
            this.base === null ||
            this.general4 === null ||
            this.applying
        )
            return;
        const channels: ApplyRequest['channels'] = [];
        if (this.general4Changed) {
            channels.push({ ...GENERAL4, kind: 'set', price: this.general4 });
        }
        for (const p of this.plan) {
            if (!p.changed) continue;
            channels.push({
                channel: p.channel,
                instance: p.instance,
                label: p.label,
                kind: p.kind,
                price: p.next,
            });
        }
        const req: ApplyRequest = {
            pid: this.pid,
            base: this.baseChanged && this.canEditBase ? this.base : null,
            channels,
        };
        if (req.base === null && channels.length === 0) {
            this.statusText = 'ไม่มีราคาที่ต้องเปลี่ยน';
            this.cdref.detectChanges();
            return;
        }
        this.applying = true;
        this.results = null;
        this.statusText = 'กำลังกรอกราคาลงฟอร์ม lnwshop ...';
        this.cdref.detectChanges();
        this.post({ action: 'apply-prices', param: req });
    }

    private resize(): void {
        // ให้ host ปรับความสูง iframe ตามเนื้อหา (host รองรับ status 'resize' อยู่แล้ว)
        setTimeout(() =>
            this.post({
                status: 'resize',
                height: document.body.scrollHeight + 8,
            }),
        );
    }
}
